import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { API } from '@aws-amplify/api';
import { Auth } from '@aws-amplify/auth';
import { NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import {
  DeepDeleteCampaignSuccess,
  LoadAffectedCampaignsSuccess,
  LoadCampaignSuccess,
  LoadMyCampaignsSuccess,
  ManageCampaign,
  PostSaveCampaignComplete,
  PostSaveCampaignFail,
  PromotionSuccess
} from 'src/app/features/campaigns/redux/campaign.actions';
import { InsightsState } from 'src/app/features/insights/redux/insights.state';
import { Campaign } from 'src/app/models/campaign';
import { Insight } from 'src/app/models/insight';
import { APIS } from 'src/app/shared/constants/api-constants';
import { AmplifyServiceBuilder } from 'src/app/shared/utils/amplify.service.builder';
import { ENVIRONMENT } from 'src/environments/environment';

@Injectable({ providedIn: 'any' })
export class CampaignService {
  public campaigns_api = APIS.CAMPAIGNS;
  public jobs_api = APIS.CAMPAIGN_JOBS;
  public individual_jobs_api = APIS.SINGLE_JOBS;
  public campaign_deliveries_api = APIS.CAMPAIGN_DELIVERIES;

  public insight$: Observable<any> = inject(Store).select(InsightsState.allInsights);
  public inactiveInsight$: Observable<any> = inject(Store).select(InsightsState.insightsInactive);

  public signedRequests: any;
  public owner: string;
  public allKeys: string[] = [];
  public selectedKeys: string[] = [];
  public type = 'campaigns';

  public insights;
  public inactiveInsights;

  public campaigns: any;

  // promotion process flags
  public promotionResponse: any = false;
  public promotionError: any = false;
  public promotionSpinner: any = false;

  constructor(
    private store: Store,
    private toastrService: NbToastrService,
    private router: Router,
    private utilityService: UtilityService
  ) {
    this.insight$.subscribe((data) => (this.insights = data));
    this.inactiveInsight$.subscribe((data) => (this.inactiveInsights = data));
  }

  public loadCampaignsAmplify(reload?) {
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true);

    if (requestData) {
      API.get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns`, requestData)
        .then((response) => {
          if (reload) {
            this.toastrService.show(status, $localize`Fetching Campaigns Complete`, {
              status: 'success'
            });
          }
          this.store.dispatch(new LoadCampaignSuccess(response));
        })
        .catch((error) => {
          this.toastrService.show(status, $localize`Fetching Campaigns Failed`, {
            status: 'danger'
          });
          console.error(error);
        });
    } else {
      this.router.navigate(['/login'], { replaceUrl: true });
    }
  }

  public loadMyCampaignsAmplify(email, reload?) {
    const queryStringParameters = { email: email };
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true, false, queryStringParameters);

    if (requestData) {
      API.get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns`, requestData)
        .then((response) => {
          if (reload) {
            this.toastrService.show(status, $localize`Fetching My Campaigns Complete`, {
              status: 'success'
            });
          }
          response.campaigns.forEach((element) => {
            this.addInsightInfo(element);
          });
          this.store.dispatch(new LoadMyCampaignsSuccess(response));
        })
        .catch((error) => {
          this.toastrService.show(status, $localize`Fetching My Campaigns Failed`, {
            status: 'danger'
          });
          console.error(error);
        });
    } else {
      this.router.navigate(['/login'], { replaceUrl: true });
    }
  }

  public loadAffectedCampaignsAmplify(campaign: Campaign, reload?) {
    const queryStringParameters = { campaign_id: campaign.id };
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true, false, queryStringParameters);
    this.fetchAffectedCampaigns(
      API.get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns/deep-delete`, requestData),
      $localize`Fetching Affected Campaigns Complete`,
      $localize`Fetching Affected Campaigns Failed`,
      reload
    );
  }

  public loadAffectedCampaignsByInsightAmplify(insight: Insight, reload?) {
    const queryStringParameters = { trigger_id: insight.id };
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true, false, queryStringParameters);
    this.fetchAffectedCampaigns(
      API.get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns/deep-delete`, requestData),
      $localize`Fetching Affected Campaigns By Insight Complete`,
      $localize`Fetching Affected Campaigns By Insight Failed`,
      reload
    );
  }

  //Activate is used when user wants to activate Campaign and Insight from View Campaign component
  public postCampaignsAmplify(campaign: Campaign, edit?: boolean, activate?: boolean) {
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true, campaign);
    API.post(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns`, requestData)
      .then((response) => {
        if (activate) {
          this.store.dispatch(new ManageCampaign(response));
          this.toastrService.show(status, $localize`Campaign activation complete`, {
            status: 'success'
          });
        } else if (activate !== undefined) {
          this.store.dispatch(new ManageCampaign(response));
          this.toastrService.show(status, $localize`Campaign deactivation complete`, {
            status: 'success'
          });
        } else {
          this.store.dispatch(new PostSaveCampaignComplete(edit, response));
        }
      })
      .catch((error) => {
        this.store.dispatch(new PostSaveCampaignFail(error));
      });
  }

  public deepDeleteCampaignAmplify(payload) {
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true, payload);
    API.post(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns/deep-delete`, requestData)
      .then((response) => {
        this.toastrService.show(status, $localize`Deep Delete Campaign ${payload.campaign_id} Success`, {
          status: 'success'
        });
        this.store.dispatch(new DeepDeleteCampaignSuccess(payload));
      })
      .catch((error) => {
        console.error(error);
        const message = this.utilityService.convertErrorToMessage(error);
        this.toastrService.show(status, $localize`Deep Delete Campaign ${payload.campaign_id} Failed: ${message}`, {
          status: 'danger'
        });
      });
  }

  public async postPromoteCampaign(campaign_id: string, username: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await Auth.currentSession().then((res) => {
      const accessToken = res.getAccessToken();
      const token = accessToken.getJwtToken();
      this.postPromoteCampaignEndpoint(campaign_id, username, token);
    });
  }

  public postPromoteCampaignEndpoint(campaign_id: string, username: string, token: string): any {
    // Promotion API Request
    const body = { campaign_id, promoter_username: username };
    const requestData = AmplifyServiceBuilder(this.campaigns_api, true, body);

    this.promotionSpinner = true;
    API.post(this.campaigns_api, `/${ENVIRONMENT.name}/promotion?token=${token}`, requestData)
      .then((response) => {
        this.promotionResponse = response;
        this.promotionSpinner = false;
        this.toastrService.show(status, $localize`Campaign has been promoted.`, {
          status: 'success'
        });
        this.store.dispatch(new PromotionSuccess('success'));
      })
      .catch((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        (this.promotionError = this.utilityService.convertErrorToMessage(error)), (this.promotionSpinner = false);
      });
  }

  public getCampaignDeliveries(campaign_id, startDate?, endDate?) {
    const queryStringParameters = {
      campaign_id,
      start_date: startDate,
      end_date: endDate
    };
    const requestData = AmplifyServiceBuilder(this.campaign_deliveries_api, true, false, queryStringParameters);

    this.toastrService.show(status, $localize`Fetching deliveries for ${campaign_id}`, {
      status: 'info'
    });

    return API.get(this.campaign_deliveries_api, `/${ENVIRONMENT.name}/deliveries`, requestData);
  }

  public runCampaignJob(campaign) {
    return new Observable<any>((observer) => {
      const requestData = AmplifyServiceBuilder(this.jobs_api, true);

      API.post(this.jobs_api, `/${ENVIRONMENT.name}/jobs/campaigns/` + campaign.id, requestData)
        .then((response) => {
          observer.next(response);
          this.toastrService.show(status, $localize`Starting Campaign ${campaign.id} Success`, { status: 'info' });
          observer.complete();
        })
        .catch((error) => {
          console.error(error);
          const message = this.utilityService.convertErrorToMessage(error);
          this.toastrService.show(status, $localize`Starting Campaign ${campaign.id} Failed: ${message}`, {
            status: 'danger'
          });
        });
    });
  }

  public terminateCampaignJob(job_id, terminating_reason) {
    const body = { reason: terminating_reason ? terminating_reason : 'User Cancelled' };

    return new Observable<any>((observer) => {
      const requestData = AmplifyServiceBuilder(this.jobs_api, true, body);

      API.del(this.individual_jobs_api, `/${ENVIRONMENT.name}/jobs/` + job_id, requestData)
        .then((response) => {
          observer.next(response);
          observer.complete();
        })
        .catch((error) => {
          console.error(error);
          const message = this.utilityService.convertErrorToMessage(error);
          this.toastrService.show(status, $localize`Stopping Campaign ${job_id} Failed ${message}`, {
            status: 'danger'
          });
        });
    });
  }

  public addInsightInfo(item) {
    const insight = this.insights.find(({ id }) => id === item.trigger_id);
    if (insight) {
      item.insight = insight;
    }

    return item;
  }

  private fetchAffectedCampaigns(
    promise: Promise<any>,
    successMessage: string,
    failureMessage: string,
    reload?: boolean
  ) {
    promise
      .then((response) => {
        if (reload) {
          this.toastrService.show(status, successMessage, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadAffectedCampaignsSuccess(response));
      })
      .catch((error) => {
        this.toastrService.show(status, failureMessage, {
          status: 'danger'
        });
        console.error(error);
      });
  }
}
